import { appWithTranslation, i18n } from 'next-i18next';
import App from 'next/app';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import { GraphQLTokenService } from 'utils/api/graphqlTokenService';
import { UnauthorizedError } from 'utils/api/unauthorizedError';
import { NETWORK_STATUS, UNIFIED_GRAPH } from 'utils/constants';
import { genericToastAlert } from 'utils/toastHelpers';

import React from 'react';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';

import '@thinkific/chalk/stylesheet.css';
import { ToastContainer, toaster } from '@thinkific/toga-react';

import { CommunityProvider } from 'components/CommunityContext';
import ErrorBoundary from 'components/ErrorBoundary';
import SiteLayout from 'components/Layouts/SiteLayout';
import RedirectUser from 'components/RedirectUser';
import { SiteFeaturesProvider } from 'components/SiteFeaturesContext';
import { ChatProvider } from 'components/new/chat/providers/ChatProvider/ChatProvider';

import nextI18NextConfig from '../next-i18next.config.js';
import './styles.scss';

// Analytics only works when the browser has loaded.
// Dynamic import used here to ensure this module is loaded after the browser has loaded
// https://nextjs.org/docs/advanced-features/dynamic-import
const TrackingLayer = dynamic(() => import('components/TrackingLayer/TrackingLayer.tsx'), {
  ssr: false,
});

const signalError = (e) => {
  throw e;
};

const gqlTokenService = new GraphQLTokenService(signalError);

const throwAuthErrorIfNeeded = (response) => {
  if (response.status === NETWORK_STATUS.UNAUTHORIZED) {
    throw new UnauthorizedError();
  }
};

const legacyAPI = createHttpLink({
  uri: '/api/graphql',
  credentials: 'same-origin',
  headers: {
    'X-Client-ID': 'communities_web',
  },
});

const unifiedAPI = createHttpLink({
  uri: '/private/graphql',
  credentials: 'same-origin',
  headers: {
    'X-Client-ID': 'communities_web',
  },
  // fetch via gqlTokenService to ensure we have a valid token and retry any 401 requests
  fetch: (uri, options) => {
    return gqlTokenService.provideRetry(async () => {
      const response = await fetch(uri, options);
      throwAuthErrorIfNeeded(response);
      return response;
    });
  },
});

const requestLink = ApolloLink.split(
  (operation) => operation.getContext().clientName === UNIFIED_GRAPH,
  unifiedAPI,
  legacyAPI
);

const errorLink = onError(({ graphQLErrors, networkError }) => {
  // eslint-disable-next-line consistent-return
  i18n.loadNamespaces('common', () => {
    if (networkError) {
      const { statusCode } = networkError;
      if (statusCode === NETWORK_STATUS.UNAUTHORIZED) {
        return Router.replace(
          `/users/sign_in?redirect_to=${
            document.location.pathname
          }&error=${i18n.t('Please sign in to continue')}`
        );
      }
      if (statusCode === NETWORK_STATUS.FORBIDDEN) {
        return Router.replace(
          `/enrollments?error=${i18n.t('You are not authorized to see this page')}`
        );
      }
      if (statusCode === NETWORK_STATUS.NOT_FOUND || statusCode === NETWORK_STATUS.DEACTIVE) {
        return Router.replace('/404.html');
      }
      return genericToastAlert();
    }
    if (graphQLErrors) {
      graphQLErrors.forEach(() => {
        toaster.alert('Something went wrong, please try again later or contact support.', {
          autoClose: true,
        });
      });
    }
  });
});

const createMiddlewareLink = () => {
  return new ApolloLink((operation, forward) => {
    return forward(operation);
  });
};
// This is a customization of the original implementation of relayStylePagination in apollo:
//  - https://github.com/apollographql/apollo-client/blob/v3.2.7/src/utilities/policies/pagination.ts#L91
// It solves a particular bug related to pagination of comments/replies.
// If you you delete the topmost comment and then click "View previous comments", the now-topmost comment
// will be returned in the fetchMore call, which causes a comment to be duplicated in the list.
// This occurs because the "read" function in relayStylePagination() sets the startCursor to be the
// "cursor" attribute of the first comment we have in the cache. This happens on the following line:
//  - https://github.com/apollographql/apollo-client/blob/v3.2.7/src/utilities/policies/pagination.ts#L109
// When the first comment is deleted from the cache (and the backend), the startCursor is updated to be the
// cursor of the now-first comment in the cache. However, because there are less comments now in the backend,
// the comments returned before a particular cursor are all shifted by 1.
// This customization ensures that the startCursor is not updated and will always equal the value defined
// in the pageInfo.
const relayPagination = relayStylePagination(['orderBy']);
const customizedRelayPagination = {
  ...relayPagination,
  read(existing, options) {
    if (!existing) return undefined;

    const { startCursor } = existing.pageInfo;
    const originalRead = relayPagination.read(existing, options);

    return {
      ...originalRead,
      pageInfo: {
        ...originalRead.pageInfo,
        startCursor,
      },
    };
  },
};

const inMemoryCache = new InMemoryCache({
  typePolicies: {
    Community: {
      fields: {
        posts: relayStylePagination(['first', 'filter', 'orderBy']),
      },
    },
    Post: {
      fields: {
        replies: customizedRelayPagination,
      },
    },
    Reply: {
      fields: {
        replies: customizedRelayPagination,
      },
    },
    User: {
      fields: {
        enrollments: relayStylePagination(),
      },
    },
    CommunitySection: {
      fields: {
        communitySpaces: {
          merge: true,
        },
      },
    },
    CommunityUserSpaceInteraction: {
      keyFields: ['communitySpace'],
    },
    CommunitySpace: {
      keyFields: ['id'],
    },
  },
});

const apolloClient = new ApolloClient({
  link: from([errorLink, createMiddlewareLink(), requestLink]),
  cache: inMemoryCache,
});

class MyApp extends App {
  constructor(props) {
    super(props);
    this.state = {
      postListState: {
        useCacheFirst: false, // This is a magical piece of global state that makes our PostList super fun to work on.
      },
    };
  }

  static async getInitialProps({ Component, ctx }) {
    const {
      query: { communityId, spaceId },
    } = ctx;

    let pageProps = {};
    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx);
    }
    pageProps = { communityId, spaceId, ...pageProps };

    return { pageProps };
  }

  render() {
    const { Component, pageProps } = this.props;
    const { communityId, spaceId } = pageProps;

    const passedPageProps = { ...this.state, ...pageProps };
    const getLayout = Component.getLayout || ((page) => <SiteLayout>{page}</SiteLayout>);
    return (
      <ErrorBoundary>
        <ApolloProvider client={apolloClient}>
          <CommunityProvider communityId={communityId} spaceId={spaceId}>
            <SiteFeaturesProvider>
              <RedirectUser>
                <TrackingLayer />
                <ChatProvider>{getLayout(<Component {...passedPageProps} />)}</ChatProvider>
                <ToastContainer />
              </RedirectUser>
            </SiteFeaturesProvider>
          </CommunityProvider>
        </ApolloProvider>
      </ErrorBoundary>
    );
  }
}

export default appWithTranslation(MyApp, nextI18NextConfig);
